/**
 * Evaluates whether a row matches the given rule criteria.
 * @param {Object} row - The slide count row with type and slideType fields.
 * @param {Object} match - The match criteria object with type/slideType arrays.
 * @returns {boolean}
 */
export const matchesRule = (row, match) => {
  if (!match || typeof match !== 'object' || Array.isArray(match)) return false
  const fields = Object.keys(match)
  if (fields.length === 0) return false
  return fields.every((field) => Array.isArray(match[field]) && match[field].includes(row[field]))
}

/**
 * Determines if a count exceeds a limit.
 * false = no limit, 0 = block all, positive = quota (> is over).
 * @param {number} count
 * @param {number|boolean} limit
 * @returns {boolean}
 */
export const isOver = (count, limit) => (limit === false ? false : count > limit)

const evaluateDistinctTypes = (rows, distinctTypeLimit) => {
  if (!distinctTypeLimit || typeof distinctTypeLimit !== 'object') return null
  const { id, limit, exclude } = distinctTypeLimit
  const seen = new Set()
  const identities = []
  rows.forEach((row) => {
    if (!row || Number(row.count) <= 0) return
    if (exclude && matchesRule(row, exclude)) return
    const slideType = row.slideType || null
    const key = `${row.type}\0${slideType}`
    if (seen.has(key)) return
    seen.add(key)
    identities.push({ type: row.type || null, slideType })
  })
  return {
    id, count: identities.length, limit, isOver: identities.length > limit, identities,
  }
}

/**
 * Evaluates slide counts against a ruleset.
 * @param {Array<{type, slideType, count}>} slideCounts
 * @param {Object} slideLimits - { rules: [{id, match: {type?, slideType?}, limit}], default: number|false }
 * @returns {{distinctTypes: ({id, count, limit, isOver, identities: Array<{type, slideType}>}|null), perRule: Array<{id, count, limit, isOver}>, unmatched: {count, limit, isOver, rows: Array<{type, slideType, count, limit, isOver}>}, isOverCapacity: boolean}}
 */
export const evaluateSlideLimits = (slideCounts = [], slideLimits = {}) => {
  const rules = Array.isArray(slideLimits.rules) ? slideLimits.rules : []
  const defaultLimit = slideLimits.default
  const perRule = rules.map((rule) => ({
    id: rule.id, count: 0, limit: rule.limit, isOver: false,
  }))
  const unmatchedRows = []

  const rows = Array.isArray(slideCounts) ? slideCounts : []
  rows.forEach((row) => {
    const count = Number(row && row.count) || 0
    const ruleIndex = rules.findIndex((rule) => matchesRule(row, rule.match))
    if (ruleIndex === -1) {
      unmatchedRows.push({
        type: (row && row.type) || null,
        slideType: (row && row.slideType) || null,
        count,
        limit: defaultLimit,
        isOver: isOver(count, defaultLimit),
      })
    } else {
      perRule[ruleIndex].count += count
    }
  })

  const perRuleWithStatus = perRule.map((rule) => ({
    ...rule,
    isOver: isOver(rule.count, rule.limit),
  }))
  const unmatched = {
    count: unmatchedRows.reduce((sum, row) => sum + row.count, 0),
    limit: defaultLimit,
    isOver: unmatchedRows.some((row) => row.isOver),
    rows: unmatchedRows,
  }
  const distinctTypes = evaluateDistinctTypes(rows, slideLimits.distinctTypeLimit)
  const isOverCapacity = perRuleWithStatus.some((rule) => rule.isOver)
    || unmatched.isOver
    || Boolean(distinctTypes && distinctTypes.isOver)

  return {
    perRule: perRuleWithStatus, unmatched, distinctTypes, isOverCapacity,
  }
}
